import React, { useRef, useState, useEffect } from 'react';

import { useRecoilState, RecoilState, useResetRecoilState, useRecoilValue } from 'recoil';
import { SortingRule } from 'react-table';
import { Box, DataTable, DataTableColumns, TableState } from '@tq/ui-components';

import { DataTablePagination } from 'components';

import {
  manageConsulteeSortByDescendingFilterState,
  manageConsulteeSortByColumnFilterState,
} from 'store';
import { dataTableStyle } from '../../styles';
import { Consultee, PaginationInfo } from 'types';

interface UserTableProps {
  paginationInfo?: PaginationInfo;
  users?: Consultee[];
  columns: DataTableColumns<Consultee>[];
  pageNumberState: RecoilState<number>;
  pageSizeState: RecoilState<number>;
  editingState: RecoilState<number | undefined>;
}

export const UserTable: React.FC<UserTableProps> = ({
  paginationInfo,
  users,
  columns,
  pageNumberState,
  pageSizeState,
  editingState,
}) => {
  const tableRef = useRef(null);
  const [orderByDescending, setOrderBy] = useRecoilState(
    manageConsulteeSortByDescendingFilterState
  );
  const [orderByValue, setOrderByValue] = useRecoilState(
    manageConsulteeSortByColumnFilterState
  );
  const resetEditingState = useResetRecoilState(editingState);
  const pageSize = useRecoilValue(pageSizeState);
  const pageNumber = useRecoilValue(pageNumberState);

  const [sortBy, setSortBy] = useState<SortingRule<Consultee>[]>([
    {
      id: orderByValue,
      desc: orderByDescending,
    },
  ]);

  const handleStateChange = (state: TableState<Consultee>) => {
    resetEditingState();
    if (!state.sortBy.length) return;
    const newOrderBy = state.sortBy?.[0]?.desc;
    const newOrderByValue = state.sortBy?.[0]?.id;
    if (state.sortBy !== sortBy) {
      setSortBy(state.sortBy);
    }
    if (newOrderByValue) {
      setOrderBy(newOrderBy);
      setOrderByValue(newOrderByValue);
    }
  };

  useEffect(() => {
    resetEditingState();
  }, [pageSize, pageNumber, resetEditingState]);

  return (
    <Box sx={dataTableStyle}>
      <DataTable
        id='usersTable'
        columns={columns}
        data={users ?? []}
        disableSortRemove={true}
        disablePagination={true}
        enableOverflowScrollingContainer={true}
        onStateChange={handleStateChange}
        size='sm'
        whiteSpace='normal'
        sortBy={sortBy}
        wordBreak='break-word'
        marginBottom={8}
        manualSortBy={true}
      />
      <DataTablePagination
        paginationInfo={paginationInfo}
        pageNumberState={pageNumberState}
        pageSizeState={pageSizeState}
        tableRef={tableRef}
        tableScrollHeaderOffset={167}
      />
    </Box>
  );
};
